import { MathUtils, PerspectiveCamera, Spherical, Vector2, Vector3 } from "three/webgpu";
import type { FocusState } from "../../store/nexusStore";
import { CameraFramingPolicy, type CameraFrame, type CameraMode } from "../camera/CameraFramingPolicy";
import {
  CAMERA_MOTION as M,
  DEFAULT_FOCUS_DIRECTION,
  FOCUS_ROTATE_PHI,
  FOCUS_ROTATE_THETA,
  captureBlackHoleOrbit,
  captureSceneBearing,
  nearestEquivalentTheta,
  rebaseOrbitTheta,
  rotateDirection,
} from "../camera/CameraMotionConfig";
import { CARTOGRAPHY_NORMAL, CARTOGRAPHY_UP, HERO_POSITION, HERO_TARGET } from "../config/worldFrame";
import { resolveViewportFraming, type ViewportFramingProfile } from "../camera/ViewportFramingPolicy";

const COSMOS_TARGET = HERO_TARGET.clone();
const COSMOS_POSITION = HERO_POSITION.clone();

export class CameraDirector {
  readonly camera: PerspectiveCamera;
  private readonly framing = new CameraFramingPolicy();
  private readonly targetPosition = new Vector3();
  private readonly targetLookAt = new Vector3();
  private readonly currentLookAt = new Vector3();
  private readonly scratchPosition = new Vector3();
  private readonly scratchDirection = new Vector3();
  private readonly scratchOffset = new Vector3();
  private readonly demoArcDirection = new Vector3();
  private readonly scratchSpherical = new Spherical();
  private readonly parallax = new Vector2();
  private readonly cosmosOrbit = new Spherical(M.cosmosRadius, M.cosmosPhi, M.cosmosTheta);
  private readonly cosmosOrbitTarget = new Spherical(M.cosmosRadius, M.cosmosPhi, M.cosmosTheta);
  private readonly blackHoleOrbit = new Spherical(14.8, M.blackHoleFocusPhi, M.blackHoleFocusTheta);
  private readonly blackHoleOrbitTarget = new Spherical(14.8, M.blackHoleFocusPhi, M.blackHoleFocusTheta);
  private readonly focusDirection = DEFAULT_FOCUS_DIRECTION.clone();
  private readonly focusDirectionTarget = DEFAULT_FOCUS_DIRECTION.clone();
  private readonly focusHomeDirection = DEFAULT_FOCUS_DIRECTION.clone();
  private readonly sceneBearingDirection = DEFAULT_FOCUS_DIRECTION.clone();
  private focus: FocusState = { kind: "cosmos" };
  private frame: CameraFrame = { mode: "cosmos", radius: 1 };
  private focusedTargetPosition = new Vector3();
  private focusedTargetArchetype: "stellar" | "black-hole" = "stellar";
  private reducedMotion = false;
  private idleMode = false;
  private accessibleMode = false;
  private idleElapsed = 0;
  private overviewThetaVelocity = 0;
  private overviewPhiVelocity = 0;
  private transitionEnergy = 0;
  private focusDistanceScale = 1;
  private viewportFraming: ViewportFramingProfile = resolveViewportFraming(1600, 900);
  private demoTourActive = false;
  private demoArcSign = 1;

  constructor(aspect: number) {
    this.camera = new PerspectiveCamera(60, aspect, 0.1, 1000);
    this.camera.position.copy(COSMOS_POSITION);
    this.currentLookAt.copy(COSMOS_TARGET);
    this.camera.lookAt(COSMOS_TARGET);
  }

  setIdleMode(idle: boolean) {
    this.idleMode = idle;
    if (!idle) this.idleElapsed = 0;
  }
  setAccessibleMode(enabled: boolean) {
    this.accessibleMode = enabled;
    if (enabled) this.parallax.set(0, 0);
  }
  setDemoTourActive(active: boolean) {
    this.demoTourActive = active;
  }
  getCameraDistanceTo(target: Vector3) {
    return this.camera.position.distanceTo(target);
  }

  getSemanticCameraDistanceTo(target: Vector3) {
    const physicalDistance = this.getCameraDistanceTo(target);
    const viewportScale =
      this.focus.kind === "cosmos"
        ? this.viewportFraming.overviewDistanceScale
        : this.viewportFraming.focusDistanceScale;
    return physicalDistance / Math.max(viewportScale, M.epsilon);
  }
  getMode(): CameraMode {
    return this.frame.mode;
  }

  setReducedMotion(reduced: boolean) {
    this.reducedMotion = reduced;
    if (reduced) {
      this.overviewThetaVelocity = 0;
      this.overviewPhiVelocity = 0;
      this.transitionEnergy = 0;
    }
  }

  setFocus(
    focus: FocusState,
    targetPosition: Vector3 | null,
    archetype: "stellar" | "black-hole" = "stellar",
    frame: CameraFrame = {
      mode:
        focus.kind === "cosmos"
          ? "cosmos"
          : archetype === "black-hole"
            ? "singularity"
            : focus.kind === "orbital"
              ? "orbital-follow"
              : "inspection",
      radius: 1,
    },
  ) {
    const previousMode = this.frame.mode;
    const previousFocus = this.focus;
    this.focus = focus;
    this.frame = frame;
    this.focusDistanceScale = 1;
    this.focusedTargetArchetype = archetype;
    if (targetPosition) this.focusedTargetPosition.copy(targetPosition);
    const focusIdentityChanged =
      previousFocus.kind !== focus.kind ||
      (previousFocus.kind === "node" && focus.kind === "node" && previousFocus.nodeId !== focus.nodeId) ||
      (previousFocus.kind === "orbital" && focus.kind === "orbital" && previousFocus.orbitalId !== focus.orbitalId) ||
      (previousFocus.kind === "node" && focus.kind === "orbital") ||
      (previousFocus.kind === "orbital" && focus.kind === "node");
    if (previousMode !== frame.mode || focusIdentityChanged) {
      this.transitionEnergy = this.reducedMotion ? 0 : 1;
      if (this.demoTourActive) this.demoArcSign *= -1;
    }

    if (focus.kind === "cosmos") {
      this.captureOverviewFromCurrentCamera();
      this.overviewThetaVelocity = 0;
      this.overviewPhiVelocity = 0;
      return;
    }

    captureSceneBearing(this.camera.position, COSMOS_TARGET, this.scratchDirection, this.sceneBearingDirection);
    if (archetype === "black-hole") {
      const focusDistance = this.framing.distance(frame);
      if (previousFocus.kind === "cosmos") this.captureBlackHoleOrbit(focusDistance);
      else
        this.captureBlackHoleOrbit(
          Math.max(
            M.blackHoleMinRadius,
            Math.min(M.blackHoleMaxRadius, this.camera.position.distanceTo(this.focusedTargetPosition)),
          ),
        );
      this.blackHoleOrbitTarget.radius = focusDistance;
      return;
    }

    this.focusDirection.copy(this.sceneBearingDirection).normalize();
    this.focusDirectionTarget.copy(this.sceneBearingDirection).normalize();
    this.focusHomeDirection.copy(this.sceneBearingDirection).normalize();
  }

  updateFocusTarget(targetPosition: Vector3 | null) {
    if (this.focus.kind === "cosmos" || !targetPosition) return;
    this.focusedTargetPosition.copy(targetPosition);
  }

  setParallax(pointerX: number, pointerY: number) {
    if (this.reducedMotion || this.accessibleMode || this.focus.kind !== "cosmos") {
      this.parallax.set(0, 0);
      return;
    }
    this.parallax.set(pointerX, pointerY);
  }

  rotateView(deltaX: number, deltaY: number) {
    if (this.reducedMotion) return;
    if (this.focus.kind === "cosmos") {
      const thetaDelta = -deltaX * M.overviewRotateTheta;
      const phiDelta = deltaY * M.overviewRotatePhi;
      this.cosmosOrbitTarget.theta += thetaDelta;
      this.cosmosOrbitTarget.phi = MathUtils.clamp(
        this.cosmosOrbitTarget.phi + phiDelta,
        M.cosmosMinPhi,
        M.cosmosMaxPhi,
      );
      const inertialTheta = MathUtils.euclideanModulo(thetaDelta + Math.PI, Math.PI * 2) - Math.PI;
      this.overviewThetaVelocity = MathUtils.lerp(this.overviewThetaVelocity, inertialTheta * 0.16, 0.65);
      this.overviewPhiVelocity = MathUtils.lerp(this.overviewPhiVelocity, phiDelta * 0.16, 0.65);
      return;
    }
    if (this.focusedTargetArchetype === "black-hole") {
      this.blackHoleOrbitTarget.theta -= deltaX * M.blackHoleRotateTheta;
      this.blackHoleOrbitTarget.phi = MathUtils.clamp(
        this.blackHoleOrbitTarget.phi + deltaY * M.blackHoleRotatePhi,
        M.blackHoleMinPhi,
        M.blackHoleMaxPhi,
      );
      return;
    }
    this.rotateFocusedView(-deltaX * FOCUS_ROTATE_THETA, deltaY * FOCUS_ROTATE_PHI);
  }

  zoomView(deltaY: number) {
    if (this.focus.kind === "cosmos") {
      const minimumRadius = M.cosmosMinRadius / this.viewportFraming.overviewDistanceScale;
      this.cosmosOrbitTarget.radius = MathUtils.clamp(
        this.cosmosOrbitTarget.radius + deltaY * 0.02,
        minimumRadius,
        M.cosmosMaxRadius,
      );
      return;
    }
    if (this.focusedTargetArchetype === "black-hole") {
      this.blackHoleOrbitTarget.radius = MathUtils.clamp(
        this.blackHoleOrbitTarget.radius + deltaY * 0.012,
        M.blackHoleMinRadius,
        M.blackHoleMaxRadius,
      );
      return;
    }
    this.focusDistanceScale = MathUtils.clamp(this.focusDistanceScale + deltaY * 0.0012, 0.56, 1.38);
  }

  nudgeView(yaw: number, pitch: number) {
    if (this.reducedMotion) return;
    if (this.focus.kind === "cosmos") {
      this.cosmosOrbitTarget.theta += yaw;
      this.cosmosOrbitTarget.phi = MathUtils.clamp(this.cosmosOrbitTarget.phi + pitch, M.cosmosMinPhi, M.cosmosMaxPhi);
      return;
    }
    if (this.focusedTargetArchetype === "black-hole") {
      this.blackHoleOrbitTarget.theta += yaw;
      this.blackHoleOrbitTarget.phi = MathUtils.clamp(
        this.blackHoleOrbitTarget.phi + pitch,
        M.blackHoleMinPhi,
        M.blackHoleMaxPhi,
      );
      return;
    }
    this.rotateFocusedView(yaw, pitch);
  }

  resetView() {
    if (this.focus.kind === "cosmos") {
      this.cosmosOrbitTarget.radius = M.cosmosRadius;
      this.cosmosOrbitTarget.phi = M.cosmosPhi;
      this.cosmosOrbitTarget.theta = M.cosmosTheta;
      this.overviewThetaVelocity = 0;
      this.overviewPhiVelocity = 0;
      this.parallax.set(0, 0);
      return;
    }
    if (this.focusedTargetArchetype === "black-hole") {
      this.blackHoleOrbitTarget.radius = this.framing.distance(this.frame);
      this.blackHoleOrbitTarget.phi = M.blackHoleFocusPhi;
      this.blackHoleOrbitTarget.theta = M.blackHoleFocusTheta;
      return;
    }
    this.focusDirectionTarget.copy(this.focusHomeDirection);
    this.focusDirection.copy(this.focusHomeDirection);
  }

  resize(width: number, height: number, coarsePointer = false) {
    this.viewportFraming = resolveViewportFraming(width, height, coarsePointer);
    this.camera.aspect = width / Math.max(height, 1);
    this.camera.fov = this.viewportFraming.fieldOfView;
    this.camera.updateProjectionMatrix();
    return this.viewportFraming;
  }

  update(delta: number) {
    this.idleElapsed = this.idleMode && !this.reducedMotion && !this.accessibleMode ? this.idleElapsed + delta : 0;
    const overviewSmoothing = this.reducedMotion ? 1 : 1 - Math.exp(-delta * 2.65);
    const focusResponsiveness = this.demoTourActive
      ? this.frame.mode === "orbital-follow"
        ? 3.9
        : 3.15
      : this.frame.mode === "orbital-follow"
        ? 6.8
        : 5.2;
    const focusSmoothing = this.reducedMotion ? 1 : 1 - Math.exp(-delta * focusResponsiveness);
    this.transitionEnergy *= Math.exp(-delta * (this.demoTourActive ? 1.18 : 2.35));
    const demoTravelCurve = this.demoTourActive
      ? Math.sin((1 - MathUtils.clamp(this.transitionEnergy, 0, 1)) * Math.PI)
      : 0;

    if (this.focus.kind !== "cosmos" && this.focusedTargetArchetype === "black-hole") {
      rebaseOrbitTheta(this.blackHoleOrbit, this.blackHoleOrbitTarget);
      if (this.demoTourActive && !this.reducedMotion && !this.accessibleMode) {
        this.blackHoleOrbitTarget.theta += delta * M.demoBlackHoleRotateTheta;
        this.blackHoleOrbitTarget.phi = MathUtils.clamp(
          M.blackHoleFocusPhi + Math.sin(this.idleElapsed * 0.8) * 0.08,
          M.blackHoleMinPhi,
          M.blackHoleMaxPhi,
        );
      }
      this.blackHoleOrbit.radius += (this.blackHoleOrbitTarget.radius - this.blackHoleOrbit.radius) * focusSmoothing;
      this.blackHoleOrbit.phi += (this.blackHoleOrbitTarget.phi - this.blackHoleOrbit.phi) * focusSmoothing;
      this.blackHoleOrbit.theta += (this.blackHoleOrbitTarget.theta - this.blackHoleOrbit.theta) * focusSmoothing;
      this.scratchPosition
        .setFromSpherical(this.blackHoleOrbit)
        .multiplyScalar(this.viewportFraming.focusDistanceScale)
        .add(this.focusedTargetPosition);
      this.targetPosition.copy(this.scratchPosition);
      if (demoTravelCurve > 0.001) {
        this.demoArcDirection
          .copy(this.targetPosition)
          .sub(this.focusedTargetPosition)
          .normalize()
          .cross(CARTOGRAPHY_UP)
          .normalize();
        this.targetPosition.addScaledVector(
          this.demoArcDirection,
          this.blackHoleOrbit.radius * M.demoTravelArcRatio * demoTravelCurve * this.demoArcSign,
        );
        this.targetPosition.addScaledVector(
          CARTOGRAPHY_UP,
          this.blackHoleOrbit.radius * M.demoTravelLiftRatio * demoTravelCurve,
        );
      }
      this.targetLookAt
        .copy(this.focusedTargetPosition)
        .addScaledVector(CARTOGRAPHY_UP, -this.blackHoleOrbit.radius * this.viewportFraming.focusLiftRatio);
    } else if (this.focus.kind !== "cosmos") {
      const baseDistance = this.framing.distance(this.frame);
      const overshoot = this.framing.flybyOvershoot(this.frame.mode) * this.transitionEnergy;
      const focusDistance =
        baseDistance * this.focusDistanceScale * this.viewportFraming.focusDistanceScale * (1 + overshoot);
      this.focusDirection.lerp(this.focusDirectionTarget, focusSmoothing).normalize();
      this.targetPosition.copy(this.focusedTargetPosition).addScaledVector(this.focusDirection, focusDistance);
      if (demoTravelCurve > 0.001) {
        this.demoArcDirection.copy(this.focusDirection).cross(CARTOGRAPHY_UP).normalize();
        this.targetPosition.addScaledVector(
          this.demoArcDirection,
          focusDistance * M.demoTravelArcRatio * demoTravelCurve * this.demoArcSign,
        );
        this.targetPosition.addScaledVector(CARTOGRAPHY_UP, focusDistance * M.demoTravelLiftRatio * demoTravelCurve);
      }
      this.targetLookAt
        .copy(this.focusedTargetPosition)
        .addScaledVector(CARTOGRAPHY_UP, -focusDistance * this.viewportFraming.focusLiftRatio);
      if (this.frame.mode === "orbital-follow" && !this.reducedMotion && !this.accessibleMode) {
        this.targetPosition.addScaledVector(CARTOGRAPHY_NORMAL, Math.sin(this.idleElapsed * 0.5) * 0.04);
      }
      if (this.demoTourActive && !this.reducedMotion && !this.accessibleMode)
        this.rotateFocusedView(
          delta * M.demoFocusRotateTheta,
          Math.sin(this.idleElapsed * 0.9) * delta * M.demoFocusRotatePhi,
        );
    } else {
      rebaseOrbitTheta(this.cosmosOrbit, this.cosmosOrbitTarget);
      const shouldReturnHome = this.idleMode && !this.accessibleMode && !this.demoTourActive;
      if (shouldReturnHome) {
        const homeResponse = this.reducedMotion ? 1 : 1 - Math.exp(-delta * M.overviewIdleHomeResponse);
        const homeTheta = nearestEquivalentTheta(this.cosmosOrbitTarget.theta, M.cosmosTheta);
        this.cosmosOrbitTarget.radius += (M.cosmosRadius - this.cosmosOrbitTarget.radius) * homeResponse;
        this.cosmosOrbitTarget.phi += (M.cosmosPhi - this.cosmosOrbitTarget.phi) * homeResponse;
        this.cosmosOrbitTarget.theta += (homeTheta - this.cosmosOrbitTarget.theta) * homeResponse;
        this.overviewThetaVelocity *= Math.exp(-delta * 9.2);
        this.overviewPhiVelocity *= Math.exp(-delta * 9.2);
        this.parallax.multiplyScalar(this.reducedMotion ? 0 : Math.exp(-delta * M.overviewIdleParallaxResponse));
        if (Math.abs(this.overviewThetaVelocity) < 0.00001) this.overviewThetaVelocity = 0;
        if (Math.abs(this.overviewPhiVelocity) < 0.00001) this.overviewPhiVelocity = 0;
      } else if (!this.reducedMotion) {
        const autoOrbit = this.demoTourActive && !this.accessibleMode ? M.overviewAutoOrbitTheta : 0;
        this.cosmosOrbitTarget.theta += this.overviewThetaVelocity + delta * autoOrbit;
        this.cosmosOrbitTarget.phi = MathUtils.clamp(
          this.cosmosOrbitTarget.phi + this.overviewPhiVelocity,
          M.cosmosMinPhi,
          M.cosmosMaxPhi,
        );
        this.overviewThetaVelocity *= Math.exp(-delta * 6.6);
        this.overviewPhiVelocity *= Math.exp(-delta * 6.6);
        if (Math.abs(this.overviewThetaVelocity) < 0.00001) this.overviewThetaVelocity = 0;
        if (Math.abs(this.overviewPhiVelocity) < 0.00001) this.overviewPhiVelocity = 0;
      }
      this.cosmosOrbit.radius += (this.cosmosOrbitTarget.radius - this.cosmosOrbit.radius) * overviewSmoothing;
      this.cosmosOrbit.phi += (this.cosmosOrbitTarget.phi - this.cosmosOrbit.phi) * overviewSmoothing;
      this.cosmosOrbit.theta += (this.cosmosOrbitTarget.theta - this.cosmosOrbit.theta) * overviewSmoothing;
      this.targetPosition
        .setFromSpherical(this.cosmosOrbit)
        .multiplyScalar(this.viewportFraming.overviewDistanceScale)
        .add(COSMOS_TARGET);
      this.targetPosition.x += this.parallax.x * 0.28;
      this.targetPosition.y += this.parallax.y * 0.2;
      this.targetLookAt.copy(COSMOS_TARGET);
      this.targetLookAt.x += this.parallax.x * 0.12;
      this.targetLookAt.y += this.parallax.y * 0.08;
    }

    const smoothing = this.focus.kind === "cosmos" ? overviewSmoothing : focusSmoothing;
    this.camera.position.lerp(this.targetPosition, smoothing);
    this.currentLookAt.lerp(this.targetLookAt, smoothing);
    this.camera.lookAt(this.currentLookAt);
  }

  private rotateFocusedView(thetaDelta: number, phiDelta: number) {
    rotateDirection(this.focusDirectionTarget, this.scratchSpherical, thetaDelta, phiDelta);
  }

  private captureOverviewFromCurrentCamera() {
    this.scratchOffset.copy(this.camera.position).sub(COSMOS_TARGET);
    if (this.scratchOffset.lengthSq() < M.epsilon) {
      this.cosmosOrbitTarget.radius = M.cosmosRadius;
      this.cosmosOrbitTarget.phi = M.cosmosPhi;
      this.cosmosOrbitTarget.theta = M.cosmosTheta;
      return;
    }
    const spherical = new Spherical().setFromVector3(this.scratchOffset);
    const baseRadius = spherical.radius / this.viewportFraming.overviewDistanceScale;
    const overviewFloor = this.viewportFraming.portrait ? M.cosmosRadius : M.cosmosMinRadius;
    this.cosmosOrbit.radius = baseRadius;
    this.cosmosOrbit.phi = MathUtils.clamp(spherical.phi, M.cosmosMinPhi, M.cosmosMaxPhi);
    this.cosmosOrbit.theta = spherical.theta;
    this.cosmosOrbitTarget.radius = MathUtils.clamp(baseRadius, overviewFloor, M.cosmosMaxRadius);
    this.cosmosOrbitTarget.phi = MathUtils.clamp(spherical.phi, M.cosmosMinPhi, M.cosmosMaxPhi);
    this.cosmosOrbitTarget.theta = spherical.theta;
  }

  private captureBlackHoleOrbit(fallbackRadius: number) {
    captureBlackHoleOrbit(
      this.camera.position,
      this.focusedTargetPosition,
      fallbackRadius,
      this.blackHoleOrbit,
      this.blackHoleOrbitTarget,
      this.scratchOffset,
    );
  }
}
